import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import usersApi from '../../api/users';
import useAuth from '../../hooks/useAuth';

// ── Helpers ───────────────────────────────────────────────────────────────────

const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400';

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}{required && ' *'}</label>
      {children}
    </div>
  );
}

// ── Building row ──────────────────────────────────────────────────────────────

function BuildingRow({ building, onRemove }) {
  return (
    <div className="flex items-center justify-between py-1.5 px-3 rounded-lg hover:bg-gray-50 group">
      <span className="text-sm text-gray-700">{building.name}</span>
      <button
        onClick={() => onRemove(building.id)}
        className="text-xs text-red-400 opacity-0 group-hover:opacity-100 hover:text-red-600 transition-opacity">
        Remove
      </button>
    </div>
  );
}

// ── Add building inline form ───────────────────────────────────────────────────

function AddBuildingForm({ locationId, onAdd, isPending }) {
  const [name, setName] = useState('');
  const [open, setOpen] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onAdd({ name: name.trim(), location: locationId });
    setName('');
    setOpen(false);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
              className="mt-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium">
        + Add building
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-2 flex gap-2 items-end">
      <div className="flex-1">
        <input
          className={inputCls}
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Building name…"
          autoFocus
        />
      </div>
      <button type="submit" disabled={!name.trim() || isPending}
              className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-40">
        Add
      </button>
      <button type="button" onClick={() => { setOpen(false); setName(''); }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-600 hover:bg-gray-50">
        Cancel
      </button>
    </form>
  );
}

// ── Location card ─────────────────────────────────────────────────────────────

function LocationCard({ location, onFlash }) {
  const queryClient = useQueryClient();

  const addBuildingMutation = useMutation({
    mutationFn: usersApi.createBuilding,
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['locations'] }); onFlash('Building added.'); },
    onError:    (err) => onFlash(err.message, 'error'),
  });

  const removeBuildingMutation = useMutation({
    mutationFn: usersApi.deleteBuilding,
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['locations'] }); onFlash('Building removed.'); },
    onError:    (err) => onFlash(err.message, 'error'),
  });

  const removeLocationMutation = useMutation({
    mutationFn: () => usersApi.deleteLocation(location.id),
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['locations'] }); onFlash('Location removed.'); },
    onError:    (err) => onFlash(err.message, 'error'),
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Location header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <div>
          <p className="font-semibold text-gray-900">{location.name}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {location.buildings?.length ?? 0} building{location.buildings?.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => {
            if (window.confirm(`Remove location "${location.name}"? This cannot be undone.`)) {
              removeLocationMutation.mutate();
            }
          }}
          className="text-xs text-red-400 hover:text-red-600 font-medium">
          Remove location
        </button>
      </div>

      {/* Buildings */}
      <div className="px-5 py-3">
        {(!location.buildings || location.buildings.length === 0) ? (
          <p className="text-xs text-gray-400 italic mb-1">No buildings yet.</p>
        ) : (
          <div className="space-y-0.5">
            {location.buildings.map(b => (
              <BuildingRow
                key={b.id}
                building={b}
                onRemove={removeBuildingMutation.mutate}
              />
            ))}
          </div>
        )}

        <AddBuildingForm
          locationId={location.id}
          onAdd={addBuildingMutation.mutate}
          isPending={addBuildingMutation.isPending}
        />
      </div>
    </div>
  );
}

// ── Add location form ─────────────────────────────────────────────────────────

function AddLocationForm({ currentDistrict, isUpperAdmin, districts, onFlash }) {
  const queryClient = useQueryClient();
  const [name,       setName]       = useState('');
  const [districtId, setDistrictId] = useState(currentDistrict ?? '');
  const [open,       setOpen]       = useState(false);

  const mutation = useMutation({
    mutationFn: usersApi.createLocation,
    onSuccess:  () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setName('');
      setOpen(false);
      onFlash('Location added.');
    },
    onError: (err) => onFlash(err.message, 'error'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const district = isUpperAdmin ? districtId : currentDistrict;
    mutation.mutate({ name: name.trim(), district });
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
              className="rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 hover:border-indigo-300 hover:text-indigo-600 w-full text-center transition-colors">
        + Add Location
      </button>
    );
  }

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 space-y-3">
      <p className="text-sm font-semibold text-indigo-800">New Location</p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Field label="Name" required>
          <input className={inputCls} value={name} onChange={e => setName(e.target.value)}
                 placeholder="e.g. Fire, Ice" autoFocus required />
        </Field>
        {isUpperAdmin && (
          <Field label="District" required>
            <select className={inputCls} value={districtId} onChange={e => setDistrictId(e.target.value)} required>
              <option value="">Select district…</option>
              {districts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </Field>
        )}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={() => { setOpen(false); setName(''); }}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-white">
            Cancel
          </button>
          <button type="submit" disabled={mutation.isPending}
                  className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-40">
            {mutation.isPending ? 'Adding…' : 'Add Location'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Main panel ─────────────────────────────────────────────────────────────────

export default function DistrictPanel() {
  const { user: me } = useAuth();
  const isUpperAdmin = me?.role === 'UPPER_ADMIN';
  const [toast, setToast] = useState(null);

  const flash = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const { data: locations = [], isLoading } = useQuery({
    queryKey: ['locations'],
    queryFn:  usersApi.getLocations,
  });

  const { data: districts = [] } = useQuery({
    queryKey: ['districts'],
    queryFn:  usersApi.getDistricts,
    enabled:  isUpperAdmin,
  });

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className={`flex items-center justify-between rounded-lg border px-4 py-3 text-sm ${
          toast.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {toast.msg}
          <button onClick={() => setToast(null)} className="ml-4 opacity-50 hover:opacity-100">✕</button>
        </div>
      )}

      <p className="text-sm text-gray-500">
        Locations represent duty teams (e.g. Fire, Ice). Buildings belong to a location
        and are used to identify where an SL is on duty.
      </p>

      {isLoading ? (
        <p className="text-sm text-gray-400 text-center py-8">Loading…</p>
      ) : (
        <div className="space-y-3">
          {locations.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">No locations configured yet.</p>
          )}
          {locations.map(loc => (
            <LocationCard key={loc.id} location={loc} onFlash={flash} />
          ))}
        </div>
      )}

      <AddLocationForm
        currentDistrict={me?.district}
        isUpperAdmin={isUpperAdmin}
        districts={districts}
        onFlash={flash}
      />
    </div>
  );
}
